import WriteProvider from './WriteProvider';

/**
 * BlockchainWriteProvider — sends write transactions to the blockchain.
 *
 * All methods follow the same pattern:
 * 1. Call callbacks.onLoading(true)
 * 2. Send the transaction
 * 3. On receipt: call callbacks.onSuccess(receipt), then reload
 * 4. On error: call callbacks.onError()
 */
class BlockchainWriteProvider extends WriteProvider {
  constructor(contract, web3, getAccount) {
    super();
    this.contract = contract;
    this.web3 = web3;
    this.getAccount = getAccount; // function that returns current account
  }

  get account() {
    return this.getAccount();
  }

  // --- Helpers ---

  _sendTx(method, callbacks, { value, onReceipt } = {}) {
    const opts = { from: this.account };
    if (value) opts.value = value;

    if (callbacks.onLoading) callbacks.onLoading(true);

    method.send(opts)
      .once('receipt', async (receipt) => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (onReceipt) onReceipt(receipt);
        if (callbacks.onSuccess) callbacks.onSuccess(receipt);
        window.location.reload();
      })
      .on('error', () => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onError) callbacks.onError();
      });
  }

  // --- Anyone functions ---

  async createNewProject(projectName, artistName, projectDescription, pricePerTokenInEth, maxEditions, scriptTransactionHash, scriptType, callbacks) {
    const pricePerTokenInWei = this.web3.utils.toWei(pricePerTokenInEth);
    const projectFeeInWei = await this.contract.methods.projectFee().call();
    this._sendTx(
      this.contract.methods.anyoneCreateProject(projectName, artistName, projectDescription, pricePerTokenInWei, maxEditions, scriptTransactionHash, scriptType),
      callbacks,
      { value: projectFeeInWei }
    );
  }

  async mint(projectId, priceInWei, callbacks) {
    if (callbacks.onLoading) callbacks.onLoading(true);

    this.web3.eth.sendTransaction({
      from: this.account,
      to: this.contract.options.address,
      value: priceInWei,
      data: this.contract.methods.anyoneMintProjectEdition(projectId).encodeABI()
    })
    .once('receipt', async (receipt) => {
      const topic0 = receipt.logs[2].topics[0];
      if (topic0 === "0x35d0381720eeb0a767201805f38d34425c787d27861d1210e5086ad17df7de03") {
        const purchaser = receipt.logs[2].topics[1];
        const mintProjectId = parseInt(receipt.logs[2].topics[2]);
        const editionId = parseInt(receipt.logs[2].data);
        const tokenId = parseInt(receipt.logs[2].topics[3]);
        const transactionHash = receipt.logs[2].transactionHash;

        const mostRecentMintEvent = {
          transactionHash: transactionHash,
          returnValues: {
            purchaser: purchaser,
            projectId: mintProjectId,
            editionId: editionId,
            tokenId: tokenId,
          }
        };

        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onSuccess) callbacks.onSuccess(receipt, mostRecentMintEvent);
      }
    })
    .on('error', () => {
      if (callbacks.onLoading) callbacks.onLoading(false);
      if (callbacks.onError) callbacks.onError();
    });
  }

  // --- Admin functions ---

  async contractOwnerUpdateWebsiteUrl(newUrl, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateWebsite(newUrl), callbacks);
  }

  async contractOwnerUpdateAPIURL(newUrl, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateAPIURL(newUrl), callbacks);
  }

  async contractOwnerUpdateUseMetadataServer(decision, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateUseMetadataServer(decision), callbacks);
  }

  async contractOwnerUpdateGlobalSecondaryRoyaltyPercent(newRoyaltyPercent, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateGlobalSecondaryRoyaltyPercent(newRoyaltyPercent), callbacks);
  }

  async contractOwnerUpdateProjectFee(newProjectFeeInEth, callbacks) {
    const newProjectFeeInWei = this.web3.utils.toWei(newProjectFeeInEth);
    this._sendTx(this.contract.methods.contractOwnerUpdateProjectFee(newProjectFeeInWei), callbacks);
  }

  async contractOwnerUpdateArtistPercent(newPercent, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateArtistPercent(newPercent), callbacks);
  }

  async contractOwnerAddTeammember(newTeammember, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerAddTeammember(newTeammember), callbacks);
  }

  async contractOwnerRemoveTeammember(delTeammember, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerRemoveTeammember(delTeammember), callbacks);
  }

  async teamToggleArtistScreeningEnabled(callbacks) {
    this._sendTx(this.contract.methods.teamToggleArtistScreeningEnabled(), callbacks);
  }

  async teamAddAuthorizedArtist(newArtist, callbacks) {
    this._sendTx(this.contract.methods.teamAddAuthorizedArtist(newArtist), callbacks);
  }

  async teamRemoveAuthorizedArtist(delArtist, callbacks) {
    this._sendTx(this.contract.methods.teamRemoveAuthorizedArtist(delArtist), callbacks);
  }

  async contractOwnerUpdateCuratorAddressAndPercent(curator, percent, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateCuratorAddressAndPercent(curator, percent), callbacks);
  }

  async contractOwnerUpdateBeneficiaryAddressAndPercent(newBeneficiary, newPercent, callbacks) {
    this._sendTx(this.contract.methods.contractOwnerUpdateBeneficiaryAddressAndPercent(newBeneficiary, newPercent), callbacks);
  }

  async adminwithdraw(callbacks) {
    if (callbacks.onLoading) callbacks.onLoading(true);
    this.contract.methods.withdraw().send({ from: this.account })
      .once('receipt', async (receipt) => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onSuccess) callbacks.onSuccess(receipt);
        // Note: does NOT reload — original code called getAdminSettings + alert
      })
      .on('error', () => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onError) callbacks.onError();
      });
  }

  async releaseRoyaltiesForProject(projectId, callbacks) {
    if (callbacks.onLoading) callbacks.onLoading(true);
    this.contract.methods.releaseRoyaltiesForProject(projectId).send({ from: this.account })
      .once('receipt', async (receipt) => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onSuccess) callbacks.onSuccess(receipt);
        // Note: does NOT reload — original code called loadProject + getAdminSettings
      })
      .on('error', () => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onError) callbacks.onError();
      });
  }

  // --- Project admin functions ---

  async artistTeamUpdateProjectName(projectId, newProjectName, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectName(projectId, newProjectName), callbacks);
  }

  async artistTeamUpdateProjectDescription(projectId, newProjectDescription, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectDescription(projectId, newProjectDescription), callbacks);
  }

  async artistTeamUpdateArtistName(projectId, newArtistName, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateArtistName(projectId, newArtistName), callbacks);
  }

  async artistTeamUpdateProjectPricePerToken(projectId, newPriceInEth, callbacks) {
    const newPriceInWei = this.web3.utils.toWei(newPriceInEth);
    this._sendTx(this.contract.methods.artistTeamUpdateProjectPricePerTokenInWei(projectId, newPriceInWei), callbacks);
  }

  async artistTeamUpdateProjectMaxEditions(projectId, newMaxEditions, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectMaxEditions(projectId, newMaxEditions), callbacks);
  }

  async artistTeamUpdateProjectMaxGridDimension(projectId, newMaxGridDimension, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectMaxGridDimension(projectId, newMaxGridDimension), callbacks);
  }

  async teamToggleProjectIsActive(projectId, callbacks) {
    this._sendTx(this.contract.methods.teamToggleProjectIsActive(projectId), callbacks);
  }

  async artistTeamToggleProjectIsPaused(projectId, callbacks) {
    this._sendTx(this.contract.methods.artistTeamToggleProjectIsPaused(projectId), callbacks);
  }

  async artistTeamUpdateProjectImageURLInfo(projectId, newIPFSCID, newIPFSCIDExt, useImageURLInGridView, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectImageURLInfo(projectId, newIPFSCID, newIPFSCIDExt, useImageURLInGridView), callbacks);
  }

  async artistTeamLockProjectContractOwnerUnlockProject(projectId, newState, callbacks) {
    if (callbacks.onLoading) callbacks.onLoading(true);
    const method = newState === true
      ? this.contract.methods.artistTeamLockProject(projectId)
      : this.contract.methods.contractOwnerUnlockProject(projectId);

    method.send({ from: this.account })
      .once('receipt', async (receipt) => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onSuccess) callbacks.onSuccess(receipt);
        // Note: original code called loadBlockchainData instead of reload
      })
      .on('error', () => {
        if (callbacks.onLoading) callbacks.onLoading(false);
        if (callbacks.onError) callbacks.onError();
      });
  }

  async teamUpdateProjectCategory(projectId, category, callbacks) {
    this._sendTx(this.contract.methods.teamUpdateProjectCategory(projectId, category), callbacks);
  }

  async artistTeamUpdateProjectScriptTransactionHash(projectId, newTransactionHash, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectScriptTransactionHash(projectId, newTransactionHash), callbacks);
  }

  async artistTeamUpdateProjectScriptType(projectId, newScriptType, callbacks) {
    this._sendTx(this.contract.methods.artistTeamUpdateProjectScriptType(projectId, newScriptType), callbacks);
  }

  async teamUpdateFeaturedProject(projectId, callbacks) {
    this._sendTx(this.contract.methods.teamUpdateFeaturedProject(projectId), callbacks);
  }

  async artistUpdateProjectAdditionalPayeeInfo(projectId, beneficiary, percent, callbacks) {
    this._sendTx(this.contract.methods.artistUpdateProjectAdditionalPayeeInfo(projectId, beneficiary, percent), callbacks);
  }

  async artistUpdateProjectArtistAddress(projectId, newAddress, callbacks) {
    this._sendTx(this.contract.methods.artistUpdateProjectArtistAddress(projectId, newAddress), callbacks);
  }

  async artistOwnerUpdateTokenWithMessage(tokenId, message, callbacks) {
    if (message.length > 0) {
      this._sendTx(this.contract.methods.artistOwnerUpdateTokenWithMessage(tokenId, message), callbacks);
    }
  }
}

export default BlockchainWriteProvider;
